import React, { useState, useEffect, useRef } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import ChatCard from './ChatCard';
import Toast from './Toast';
import { saveOutreachTemplate, getOutreachTemplates, aiAutomationApi } from '../utils/aiAutomationApi';
import { getMetadata } from '../utils/encryptedStorage';
import '../styles/chat.css';

const OutreachContent = ({ 
  selectedCandidates = [], 
  allCandidates = [],
  sessionId,
  searchId,
  jdDetails = {},
  jdText = '',
  onBack,
  onStartOutreach,
  onRegenerateTemplate,
  outreachTemplate = null,
  templatesByChannel = null, // { call, email, linkedin }
  isLoadingTemplate = false,
  isSendingOutreach = false,
  companyName = '',
  selectedOutreachChannels = [] // ['gmail', 'linkedin', 'whatsapp']
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  
  // Ref for company name input to focus on validation error
  const companyNameInputRef = useRef(null);
  
  // Determine which templates to show based on selected channels (empty means all)
  const showAllChannels = selectedOutreachChannels.length === 0;
  const showEmailTemplate = showAllChannels || selectedOutreachChannels.includes('gmail');
  const showLinkedinTemplate = showAllChannels || selectedOutreachChannels.includes('linkedin');
  const showCallTemplate = showAllChannels || selectedOutreachChannels.includes('whatsapp') || selectedOutreachChannels.includes('call');

  // Local editing state for templates
  const [isEditing, setIsEditing] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateChannel, setSaveTemplateChannel] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState('');
  const [saveTemplateNotice, setSaveTemplateNotice] = useState('');
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templatePickerLoading, setTemplatePickerLoading] = useState(false);
  const [templatePickerError, setTemplatePickerError] = useState('');
  const [templateLibrary, setTemplateLibrary] = useState([]);
  const [selectedLibraryTemplate, setSelectedLibraryTemplate] = useState(null);
  const [companyNameInput, setCompanyNameInput] = useState(companyName || '');
  const [companyNameFromSettings, setCompanyNameFromSettings] = useState(false);
  const [companyNameError, setCompanyNameError] = useState(false); // For highlighting failed validation
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

  // Email signature state – loaded from settings, editable inline
  const [emailSignature, setEmailSignature] = useState({
    title: 'Best regards,',
    name: '',
    position: '',
    company: '',
    phone: '',
    email: '',
  });
  const [isEditingSignature, setIsEditingSignature] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);

  // Fetch company name and email signature from email settings on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const metadata = getMetadata() || {};
        const cid = metadata?.cid;
        if (!cid) return;
        const res = await aiAutomationApi.get(`/email/settings/${encodeURIComponent(cid)}`);
        const sig = res?.settings?.email_signature;
        if (!cancelled && sig) {
          const settingsCompany = (sig.company || '').trim();
          if (settingsCompany) {
            setCompanyNameInput(settingsCompany);
            setCompanyNameFromSettings(true);
          }
          // Populate full signature state from settings
          setEmailSignature({
            title: (sig.title || 'Best regards,').trim(),
            name: (sig.name || '').trim(),
            position: (sig.position || '').trim(),
            company: settingsCompany,
            phone: (sig.phone || '').trim(),
            email: (sig.email || '').trim(),
          });
        }
      } catch (err) {
        // Silently fail – user can still type company name manually
        console.warn('Could not load company name from email settings:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Extract JD details for default templates
  const jobTitle = jdDetails?.job_title || jdDetails?.title || jdDetails?.role || 'the role';
  const jobLocation = jdDetails?.location || jdDetails?.primary_hiring_locations || '';
  const jobExperience = jdDetails?.experience || jdDetails?.experience_years || '';
  const jobSkills = Array.isArray(jdDetails?.skills) 
    ? jdDetails.skills.slice(0, 5).join(', ') 
    : (jdDetails?.skills || '');

  const [callText, setCallText] = useState(
    (templatesByChannel && templatesByChannel.call) ||
      `Hi [CANDIDATE_NAME], this is [YOUR_NAME] from ${companyName || '[COMPANY_NAME]'}. 
I'm reaching out about a ${jobTitle} position we have open${jobLocation ? ` in ${jobLocation}` : ''}${jobExperience ? ` requiring ${jobExperience} years of experience` : ''}.${jobSkills ? ` We're looking for someone with ${jobSkills} expertise.` : ''} 
Your background looks like a strong fit for this role.
Is now a good time to chat for a couple of minutes, or is there a better time later today or tomorrow?`
  );

  const [emailText, setEmailText] = useState(
    (templatesByChannel && templatesByChannel.email) ||
      outreachTemplate ||
      `Hi [CANDIDATE_NAME],

I hope this message finds you well. We came across your profile and were impressed by your background and experience.

We have an exciting ${jobTitle} opportunity at ${companyName || '[COMPANY_NAME]'}${jobLocation ? ` in ${jobLocation}` : ''} that could be a strong match for your skills.${jobExperience ? ` This role requires approximately ${jobExperience} years of experience.` : ''}${jobSkills ? ` We're looking for candidates with expertise in ${jobSkills}.` : ''}

If you are interested and open to exploring this opportunity, we would love to hear from you. **Please attach your updated resume/CV when you reply to this email** so our team can review your profile and discuss the next steps.

Looking forward to connecting with you.`
  );

  const [linkedinText, setLinkedinText] = useState(
    (templatesByChannel && templatesByChannel.linkedin) ||
      `Hi [CANDIDATE_NAME],

I came across your profile and it looks like a great fit for a ${jobTitle} role we're hiring for at ${companyName || '[COMPANY_NAME]'}.${jobLocation ? ` The position is based in ${jobLocation}.` : ''}${jobExperience ? ` We're looking for ${jobExperience}+ years of experience.` : ''}${jobSkills ? ` Key skills: ${jobSkills}.` : ''} 
Would you be open to a quick chat this week to explore the opportunity?`
  );

  // Convert hex to rgba for gradient background
  const hexToRgba = (hex, alpha) => {
    const safe = (hex || '').replace('#', '');
    if (safe.length !== 6) return hex;
    const r = parseInt(safe.slice(0, 2), 16);
    const g = parseInt(safe.slice(2, 4), 16);
    const b = parseInt(safe.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Get selected candidates details - ensure type-agnostic comparison for IDs
  const selectedCandidateDetails = allCandidates.filter(c => {
    const cId = String(c.id || c.candidate_id || '');
    return selectedCandidates.some(id => String(id) === cId);
  });

  const emailCount = selectedCandidateDetails.filter(c => c.email).length;

  // Final template text used for sending (we use the email template as primary body)
  const templateText =
    (isEditing ? emailText : (outreachTemplate || '' )) ||
    `Hi [First Name],

I hope this message finds you well. I came across your profile and was impressed by your background and experience.

We have an exciting opportunity at [COMPANY_NAME] that could be a strong match for your skills. Would you be open to a quick chat so I can share more details?

Best,
[Your Name]`;

  useEffect(() => {
    // Only update from prop if prop has a value and settings didn't already provide one
    if (companyName && companyName.trim()) {
      setCompanyNameInput(companyName);
    }
  }, [companyName]);

  // Update templates when templatesByChannel changes (API returns new templates)
  useEffect(() => {
    if (templatesByChannel) {
      if (templatesByChannel.call) {
        setCallText(templatesByChannel.call);
      }
      if (templatesByChannel.email) {
        setEmailText(templatesByChannel.email);
      }
      if (templatesByChannel.linkedin) {
        setLinkedinText(templatesByChannel.linkedin);
      }
    }
  }, [templatesByChannel]);

  // Also update email text when outreachTemplate prop changes (for backward compatibility)
  useEffect(() => {
    if (outreachTemplate && outreachTemplate.trim() && !templatesByChannel?.email) {
      setEmailText(outreachTemplate);
    }
  }, [outreachTemplate, templatesByChannel]);
  // Keep email signature company name in sync with company name input
  useEffect(() => {
    if (companyNameInput && companyNameInput.trim()) {
      setEmailSignature(prev => ({ ...prev, company: companyNameInput.trim() }));
    }
  }, [companyNameInput]);

  // Save signature to DB via PUT /email/signature
  const handleSaveSignature = async () => {
    const metadata = getMetadata() || {};
    const cid = metadata?.cid;
    if (!cid) return;
    if (!emailSignature.company || !emailSignature.company.trim()) {
      showToast('Company name is required and cannot be removed.', 'error');
      return;
    }
    try {
      setIsSavingSignature(true);
      await aiAutomationApi.put('/email/signature', {
        cid: String(cid),
        ...emailSignature,
      });
      setIsEditingSignature(false);
      showToast('Signature saved successfully!', 'success');
    } catch (err) {
      console.error('Failed to save signature:', err);
      showToast('Failed to save signature', 'error');
    } finally {
      setIsSavingSignature(false);
    }
  };

  // Build signature preview text
  const getSignaturePreviewText = () => {
    const parts = [];
    if (emailSignature.title) parts.push(emailSignature.title);
    if (emailSignature.name) parts.push(emailSignature.name);
    if (emailSignature.position) parts.push(emailSignature.position);
    if (emailSignature.company) parts.push(emailSignature.company);
    if (emailSignature.phone) parts.push(`Phone: ${emailSignature.phone}`);
    if (emailSignature.email) parts.push(`Email: ${emailSignature.email}`);
    return parts.join('\n');
  };

  const showToast = (message, type = 'error') => {
    setToast({ open: true, message, type });
    setTimeout(() => {
      setToast({ open: false, message: '', type: 'error' });
    }, 2400);
  };

  const handleStartOutreach = async () => {
    console.log('🔘 Start Outreach button clicked');
    console.log('📋 Data being passed:', {
      templateLength: templateText?.length,
      candidatesCount: selectedCandidateDetails?.length,
      candidateIdsCount: selectedCandidates?.length,
      candidateIds: selectedCandidates,
      hasOnStartOutreach: !!onStartOutreach
    });
    
    if (!onStartOutreach) {
      console.error('❌ onStartOutreach callback is not provided!');
      return;
    }
    
    if (!templateText || !templateText.trim()) {
      console.error('❌ No template text available');
      return;
    }
    
    if (!selectedCandidates || selectedCandidates.length === 0) {
      console.error('❌ No candidates selected');
      return;
    }

    if (!companyNameInput || !companyNameInput.trim()) {
      showToast('Please enter Company Name before sending outreach.', 'error');
      // Highlight the company name field and focus on it
      setCompanyNameError(true);
      if (companyNameInputRef.current) {
        companyNameInputRef.current.focus();
        companyNameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Clear any previous error state
    setCompanyNameError(false);
    
    // Save company name to email settings if user entered it manually (not from settings)
    if (!companyNameFromSettings && companyNameInput.trim()) {
      try {
        const metadata = getMetadata() || {};
        const cid = metadata?.cid;
        if (cid) {
          const res = await aiAutomationApi.get(`/email/settings/${encodeURIComponent(cid)}`);
          const currentSettings = res?.settings || {};
          const updatedSettings = {
            ...currentSettings,
            email_signature: {
              ...(currentSettings.email_signature || {}),
              company: companyNameInput.trim(),
            },
          };
          await aiAutomationApi.post('/email/settings', { cid: String(cid), settings: updatedSettings });
          setCompanyNameFromSettings(true);
          console.log('✅ Company name saved to email settings:', companyNameInput.trim());
        }
      } catch (err) {
        console.warn('Could not save company name to email settings:', err);
      }
    }

    console.log('✅ Calling onStartOutreach callback...');
    onStartOutreach({
      template: templateText,
      linkedinTemplate: linkedinText || '',
      candidates: selectedCandidateDetails,
      candidateIds: selectedCandidates,
      companyName: companyNameInput.trim(),
    });
  };

  const getTemplateContentByChannel = (channel) => {
    if (channel === 'call') return callText;
    if (channel === 'email') return emailText;
    if (channel === 'linkedin') return linkedinText;
    return '';
  };

  const openSaveTemplateModal = (channel) => {
    setSaveTemplateChannel(channel);
    setSaveTemplateName('');
    setSaveTemplateError('');
    setSaveModalOpen(true);
  };

  const closeSaveTemplateModal = () => {
    if (isSavingTemplate) return;
    setSaveModalOpen(false);
    setSaveTemplateName('');
    setSaveTemplateChannel('');
    setSaveTemplateError('');
  };

  const handleSaveTemplate = async () => {
    const name = (saveTemplateName || '').trim();
    const content = (getTemplateContentByChannel(saveTemplateChannel) || '').trim();

    if (!name) {
      setSaveTemplateError('Template name is required');
      return;
    }

    if (!content) {
      setSaveTemplateError('Template content is empty');
      return;
    }

    try {
      setIsSavingTemplate(true);
      setSaveTemplateError('');

      const metadata = getMetadata() || {};
      const cid = metadata?.cid != null ? String(metadata.cid) : null;
      const userId = metadata?.uid || metadata?.userId || metadata?.id || null;

      await saveOutreachTemplate({
        template_name: name,
        content,
        cid,
        create_by: userId != null ? String(userId) : null,
      });

      setSaveTemplateNotice('Template saved successfully');
      closeSaveTemplateModal();
      setTimeout(() => setSaveTemplateNotice(''), 2500);
    } catch (error) {
      setSaveTemplateError(error?.message || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const getTemplateIcon = (type) => {
    if (type === 'call') return '📞';
    if (type === 'linkedin') return 'in';
    return '✉️';
  };

  const closeTemplatePicker = () => {
    if (templatePickerLoading) return;
    setTemplatePickerOpen(false);
    setTemplatePickerError('');
  };

  const handleOpenTemplatePicker = async () => {
    try {
      setTemplatePickerOpen(true);
      setTemplatePickerLoading(true);
      setTemplatePickerError('');

      const metadata = getMetadata() || {};
      const cid = metadata?.cid != null ? String(metadata.cid) : null;

      const response = await getOutreachTemplates(cid);
      const templates = Array.isArray(response?.templates) ? response.templates : [];
      setTemplateLibrary(templates);
      setSelectedLibraryTemplate(templates.length > 0 ? templates[0] : null);
    } catch (error) {
      setTemplatePickerError(error?.message || 'Failed to load templates');
      setTemplateLibrary([]);
      setSelectedLibraryTemplate(null);
    } finally {
      setTemplatePickerLoading(false);
    }
  };

  const applyLibraryTemplate = () => {
    if (!selectedLibraryTemplate) return;

    const resolvedType = (selectedLibraryTemplate.template_type || '').toLowerCase();
    const content = selectedLibraryTemplate.content || '';

    if (resolvedType === 'call') {
      setCallText(content);
    } else if (resolvedType === 'linkedin') {
      setLinkedinText(content);
    } else {
      setEmailText(content);
    }

    closeTemplatePicker();
  };

  return (
    <div className="chat_spacing_lg">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between chat_mb_lg">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm chat_transition"
          style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Candidates
        </button>
      </div>

      {/* Main Heading */}
      <h3
        className="chat_heading_md chat_font_semibold chat_mb_lg text-center"
        style={{ color: primaryColor }}
      >
        Content Ready for Outreach
      </h3>

      {/* Suggested Content for Outreach Channels */}
      <div className="chat_mb_lg">
        <div className="chat_mb_md">
          <label
            className="chat_text_sm chat_font_medium"
            style={{
              display: 'block',
              marginBottom: '8px',
              color: companyNameError ? '#EF4444' : (isDark ? '#D1D5DB' : '#374151'),
            }}
          >
            Company Name <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <input
            ref={companyNameInputRef}
            type="text"
            value={companyNameInput}
            onChange={(e) => {
              setCompanyNameInput(e.target.value);
              // Clear error state when user starts typing
              if (companyNameError && e.target.value.trim()) {
                setCompanyNameError(false);
              }
            }}
            placeholder="Enter company name"
            className="w-full"
            style={{
              borderRadius: '10px',
              border: `2px solid ${companyNameError ? '#EF4444' : (isDark ? '#4B5563' : '#D1D5DB')}`,
              backgroundColor: companyNameError 
                ? (isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)')
                : (isDark ? '#111827' : '#FFFFFF'),
              color: isDark ? '#F9FAFB' : '#111827',
              fontSize: '14px',
              padding: '10px 12px',
              outline: 'none',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
          />
        </div>

        <div className="flex items-center justify-between chat_mb_md">
          <h4 className="chat_section_heading">
            Suggested Content for Outreach Channels
          </h4>
          <button
            type="button"
            disabled={isLoadingTemplate}
            onClick={handleOpenTemplatePicker}
            className="chat_font_medium chat_transition flex flex-col items-start"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              opacity: isLoadingTemplate ? 0.7 : 1,
              cursor: isLoadingTemplate ? 'not-allowed' : 'pointer',
            }}
          >
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                fontSize: '11px',
                whiteSpace: 'nowrap',
              }}
            >
              Choose from Template
            </span>
            <div
              style={{
                marginTop: '2px',
                height: '1.5px',
                width: '100%',
                borderRadius: '999px',
                backgroundImage: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              }}
            />
          </button>
        </div>

        {/* Templates Content – three separate cards + JD card */}
        {isLoadingTemplate ? (
          <ChatCard
            variant="primary"
            size="md"
            rounded="lg"
            className="chat_mb_md"
            style={{
              backgroundColor: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
              borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
            }}
          >
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <div
                  className="animate-spin rounded-full h-5 w-5 border-b-2"
                  style={{ borderColor: primaryColor }}
                ></div>
                <span
                  className="chat_text_md"
                  style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                >
                  Generating personalized templates...
                </span>
              </div>
            </div>
          </ChatCard>
        ) : (
          <>
            {/* Call Script Card - only show if whatsapp/call channel selected */}
            {showCallTemplate && (
            <ChatCard
              variant="primary"
              size="md"
              rounded="lg"
              className="chat_mb_md"
              style={{
                backgroundColor: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
              }}
            >
              <div className="flex items-start justify-between chat_mb_sm">
                <h5
                  className="chat_font_semibold"
                  style={{
                    fontSize: '14px',
                    color: isDark ? '#F9FAFB' : '#111827',
                  }}
                >
                  Call Script
                </h5>
                {/* Save Template icon */}
                <button
                  type="button"
                  onClick={() => openSaveTemplateModal('call')}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  style={{ border: 'none', background: 'transparent' }}
                  aria-label="Save Call Script Template"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v14l-7-4-7 4V5z"
                    />
                  </svg>
                </button>
              </div>
              {isEditing ? (
                <textarea
                  className="w-full chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                    background: 'transparent',
                    borderRadius: '8px',
                    border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
                    padding: '8px',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                  value={callText}
                  onChange={(e) => setCallText(e.target.value)}
                />
              ) : (
                <div
                  className="chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                  }}
                >
                  {callText}
                </div>
              )}
            </ChatCard>
            )}

            {/* Email Template Card - only show if gmail channel selected */}
            {showEmailTemplate && (
            <ChatCard
              variant="primary"
              size="md"
              rounded="lg"
              className="chat_mb_md"
              style={{
                backgroundColor: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
              }}
            >
              <div className="flex items-start justify-between chat_mb_sm">
                <h5
                  className="chat_font_semibold"
                  style={{
                    fontSize: '14px',
                    color: isDark ? '#F9FAFB' : '#111827',
                  }}
                >
                  Email Template
                </h5>
                <button
                  type="button"
                  onClick={() => openSaveTemplateModal('email')}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  style={{ border: 'none', background: 'transparent' }}
                  aria-label="Save Email Template"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v14l-7-4-7 4V5z"
                    />
                  </svg>
                </button>
              </div>
              {isEditing ? (
                <textarea
                  className="w-full chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                    background: 'transparent',
                    borderRadius: '8px',
                    border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
                    padding: '8px',
                    minHeight: '120px',
                    resize: 'vertical',
                  }}
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                />
              ) : (
                <div
                  className="chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                  }}
                >
                  {emailText}
                </div>
              )}

              {/* Email Signature Preview/Edit — shown below the email body */}
              <div
                style={{
                  marginTop: '16px',
                  paddingTop: '12px',
                  borderTop: `1px dashed ${isDark ? '#4B5563' : '#D1D5DB'}`,
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                  <span
                    className="chat_font_semibold"
                    style={{
                      fontSize: '12px',
                      color: isDark ? '#9CA3AF' : '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Signature / Regards
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditingSignature) {
                        handleSaveSignature();
                      } else {
                        setIsEditingSignature(true);
                      }
                    }}
                    disabled={isSavingSignature}
                    className="chat_font_medium chat_transition"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '2px 8px',
                      fontSize: '12px',
                      color: primaryColor,
                      cursor: isSavingSignature ? 'not-allowed' : 'pointer',
                      opacity: isSavingSignature ? 0.6 : 1,
                    }}
                  >
                    {isSavingSignature ? 'Saving...' : isEditingSignature ? 'Save Signature' : 'Edit Signature'}
                  </button>
                </div>

                {isEditingSignature ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { key: 'title', label: 'Title', placeholder: 'Best regards,', removable: true },
                      { key: 'name', label: 'Name', placeholder: 'John Doe', removable: true },
                      { key: 'position', label: 'Position', placeholder: 'HR Manager', removable: true },
                      { key: 'company', label: 'Company *', placeholder: 'Company Name (required)', removable: false },
                      { key: 'phone', label: 'Phone', placeholder: '+1 234 567 890', removable: true },
                      { key: 'email', label: 'Email', placeholder: 'john@acme.com', removable: true },
                    ].map(({ key, label, placeholder, removable }) => (
                      <div key={key} style={{ position: 'relative' }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: isDark ? '#9CA3AF' : '#6B7280',
                            marginBottom: '2px',
                          }}
                        >
                          {label}
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="text"
                            value={emailSignature[key] || ''}
                            onChange={(e) =>
                              setEmailSignature((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            placeholder={placeholder}
                            style={{
                              flex: 1,
                              fontSize: '13px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: `1px solid ${
                                key === 'company' && !emailSignature.company?.trim()
                                  ? '#EF4444'
                                  : isDark
                                  ? '#4B5563'
                                  : '#D1D5DB'
                              }`,
                              backgroundColor: isDark ? '#111827' : '#FFFFFF',
                              color: isDark ? '#F9FAFB' : '#111827',
                              outline: 'none',
                            }}
                          />
                          {removable && emailSignature[key] && (
                            <button
                              type="button"
                              onClick={() =>
                                setEmailSignature((prev) => ({ ...prev, [key]: '' }))
                              }
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: isDark ? '#9CA3AF' : '#6B7280',
                                fontSize: '14px',
                                padding: '2px',
                                lineHeight: 1,
                              }}
                              title={`Clear ${label}`}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="chat_text_sm whitespace-pre-wrap"
                    style={{
                      color: isDark ? '#D1D5DB' : '#4B5563',
                      lineHeight: '1.6',
                      fontStyle: getSignaturePreviewText() ? 'normal' : 'italic',
                      opacity: getSignaturePreviewText() ? 1 : 0.6,
                    }}
                  >
                    {getSignaturePreviewText() || 'No signature configured. Click "Edit Signature" to add one.'}
                  </div>
                )}
              </div>
            </ChatCard>
            )}

            {/* LinkedIn Message Template Card - only show if linkedin channel selected */}
            {showLinkedinTemplate && (
            <ChatCard
              variant="primary"
              size="md"
              rounded="lg"
              className="chat_mb_md"
              style={{
                backgroundColor: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
              }}
            >
              <div className="flex items-start justify-between chat_mb_sm">
                <h5
                  className="chat_font_semibold"
                  style={{
                    fontSize: '14px',
                    color: isDark ? '#F9FAFB' : '#111827',
                  }}
                >
                  LinkedIn Message Template
                </h5>
                <button
                  type="button"
                  onClick={() => openSaveTemplateModal('linkedin')}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  style={{ border: 'none', background: 'transparent' }}
                  aria-label="Save LinkedIn Template"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v14l-7-4-7 4V5z"
                    />
                  </svg>
                </button>
              </div>
              {isEditing ? (
                <textarea
                  className="w-full chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                    background: 'transparent',
                    borderRadius: '8px',
                    border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
                    padding: '8px',
                    minHeight: '100px',
                    resize: 'vertical',
                  }}
                  value={linkedinText}
                  onChange={(e) => setLinkedinText(e.target.value)}
                />
              ) : (
                <div
                  className="chat_text_sm whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#D1D5DB' : '#4B5563',
                    lineHeight: '1.6',
                  }}
                >
                  {linkedinText}
                </div>
              )}
            </ChatCard>
            )}

            {/* JD Attached Card */}
            {jdText && (
              <ChatCard
                variant="primary"
                size="md"
                rounded="lg"
                className="chat_mb_md"
                style={{
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                  borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
                }}
              >
                <h5
                  className="chat_font_semibold chat_mb_xs"
                  style={{
                    fontSize: '14px',
                    color: isDark ? '#F9FAFB' : '#111827',
                  }}
                >
                  JD Attached
                </h5>
                <pre
                  className="chat_text_xs whitespace-pre-wrap"
                  style={{
                    color: isDark ? '#E5E7EB' : '#4B5563',
                    lineHeight: '1.5',
                  }}
                >
                  {jdText}
                </pre>
              </ChatCard>
            )}
          </>
        )}
      </div>

      {/* Edit Content + Start Outreach Buttons */}
      {saveTemplateNotice && (
        <div
          className="chat_text_sm chat_mb_md"
          style={{ color: isDark ? '#86EFAC' : '#15803D' }}
        >
          {saveTemplateNotice}
        </div>
      )}
      <div className="flex gap-3">
        {/* Edit Content (outlined) */}
        <button
          type="button"
          onClick={() => setIsEditing(prev => !prev)}
          disabled={isLoadingTemplate || isSendingOutreach}
          className="flex-1 chat_font_medium chat_text_md chat_transition"
          style={{
            backgroundColor: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#F9FAFB' : '#111827',
            borderRadius: '16px',
            padding: '12px 24px',
            border: `2px solid ${primaryColor}`,
            cursor: (isLoadingTemplate || isSendingOutreach) ? 'not-allowed' : 'pointer',
          }}
        >
          {isEditing ? 'Done Editing' : 'Edit Content'}
        </button>

        {/* Start Outreach (filled) */}
        <button
          onClick={handleStartOutreach}
          disabled={isLoadingTemplate || isSendingOutreach || !templateText}
          className="flex-1 chat_font_medium chat_text_md chat_transition"
          style={{
            backgroundColor: (isLoadingTemplate || isSendingOutreach || !templateText)
              ? (isDark ? '#374151' : '#E5E7EB')
              : primaryColor,
            color: (isLoadingTemplate || isSendingOutreach || !templateText)
              ? (isDark ? '#6B7280' : '#9CA3AF')
              : '#ffffff',
            borderRadius: '16px',
            padding: '12px 31px',
            border: 'none',
            cursor: (isLoadingTemplate || isSendingOutreach || !templateText)
              ? 'not-allowed'
              : 'pointer',
          }}
        >
          {isSendingOutreach ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Sending Outreach...
            </span>
          ) : isLoadingTemplate ? 'Generating...' : 'Start Outreach'}
        </button>
      </div>

      {templatePickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.4)' }}
          onClick={closeTemplatePicker}
        >
          <div
            className="w-full"
            style={{
              maxWidth: '920px',
              borderRadius: '12px',
              backgroundColor: isDark ? '#111827' : '#FFFFFF',
              border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between"
              style={{ padding: '14px 16px' }}
            >
              <h4
                className="chat_font_semibold"
                style={{ fontSize: '20px', color: isDark ? '#F9FAFB' : '#111827' }}
              >
                Choose Template
              </h4>
              <button
                type="button"
                onClick={closeTemplatePicker}
                disabled={templatePickerLoading}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: isDark ? '#D1D5DB' : '#4B5563',
                  fontSize: '22px',
                  lineHeight: '1',
                  cursor: templatePickerLoading ? 'not-allowed' : 'pointer',
                }}
                aria-label="Close template picker"
              >
                ×
              </button>
            </div>

            <div style={{ borderTop: `1px solid ${isDark ? '#374151' : '#E5E7EB'}` }} />

            <div
              className="grid"
              style={{
                gridTemplateColumns: '320px 1fr',
                minHeight: '420px',
              }}
            >
              <div
                style={{
                  borderRight: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
                  padding: '12px',
                  overflowY: 'auto',
                }}
              >
                {templatePickerLoading ? (
                  <div className="chat_text_sm" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
                    Loading templates...
                  </div>
                ) : templatePickerError ? (
                  <div className="chat_text_sm" style={{ color: '#EF4444' }}>
                    {templatePickerError}
                  </div>
                ) : templateLibrary.length === 0 ? (
                  <div className="chat_text_sm" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
                    No templates found for this company.
                  </div>
                ) : (
                  <div className="chat_spacing_sm">
                    {templateLibrary.map((tpl) => {
                      const isActive = selectedLibraryTemplate?.mid === tpl.mid;
                      const templateType = (tpl.template_type || 'email').toLowerCase();
                      return (
                        <button
                          key={tpl.mid}
                          type="button"
                          onClick={() => setSelectedLibraryTemplate(tpl)}
                          className="w-full text-left chat_transition"
                          style={{
                            borderRadius: '10px',
                            border: `1px solid ${isActive ? primaryColor : (isDark ? '#374151' : '#E5E7EB')}`,
                            backgroundColor: isActive ? (isDark ? 'rgba(59, 91, 219, 0.18)' : 'rgba(59, 91, 219, 0.08)') : 'transparent',
                            padding: '10px 12px',
                            cursor: 'pointer',
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '18px',
                                height: '18px',
                                fontSize: templateType === 'linkedin' ? '11px' : '12px',
                                borderRadius: '999px',
                                border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                                color: isDark ? '#E5E7EB' : '#374151',
                              }}
                            >
                              {getTemplateIcon(templateType)}
                            </span>
                            <span
                              className="chat_font_medium"
                              style={{ color: isDark ? '#F9FAFB' : '#111827', fontSize: '13px' }}
                            >
                              {tpl.template_name}
                            </span>
                          </div>
                          <div
                            className="chat_text_xs"
                            style={{ color: isDark ? '#9CA3AF' : '#6B7280', marginTop: '4px' }}
                          >
                            {templateType.toUpperCase()}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ padding: '14px 16px' }}>
                {!selectedLibraryTemplate ? (
                  <div className="chat_text_sm" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
                    Select a template to preview content.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between chat_mb_sm">
                      <div>
                        <div
                          className="chat_font_semibold"
                          style={{ color: isDark ? '#F9FAFB' : '#111827' }}
                        >
                          {selectedLibraryTemplate.template_name}
                        </div>
                        <div
                          className="chat_text_xs"
                          style={{ color: isDark ? '#9CA3AF' : '#6B7280', marginTop: '2px' }}
                        >
                          Type: {(selectedLibraryTemplate.template_type || 'email').toUpperCase()}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={applyLibraryTemplate}
                        style={{
                          border: 'none',
                          borderRadius: '10px',
                          backgroundColor: primaryColor,
                          color: '#FFFFFF',
                          padding: '8px 14px',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        Use Template
                      </button>
                    </div>
                    <div
                      className="chat_text_sm"
                      style={{
                        whiteSpace: 'pre-wrap',
                        border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
                        borderRadius: '10px',
                        backgroundColor: isDark ? '#0F172A' : '#F9FAFB',
                        color: isDark ? '#E5E7EB' : '#374151',
                        padding: '12px',
                        minHeight: '320px',
                        lineHeight: '1.6',
                        overflowY: 'auto',
                      }}
                    >
                      {selectedLibraryTemplate.content}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {saveModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.35)' }}
          onClick={closeSaveTemplateModal}
        >
          <div
            className="w-full"
            style={{
              maxWidth: '360px',
              borderRadius: '12px',
              backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
              border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between"
              style={{ padding: '14px 14px 10px 14px' }}
            >
              <h4
                className="chat_font_semibold"
                style={{ fontSize: '20px', color: isDark ? '#F9FAFB' : '#111827' }}
              >
                Save Template
              </h4>
              <button
                type="button"
                onClick={closeSaveTemplateModal}
                disabled={isSavingTemplate}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: isDark ? '#D1D5DB' : '#4B5563',
                  fontSize: '22px',
                  lineHeight: '1',
                  cursor: isSavingTemplate ? 'not-allowed' : 'pointer',
                }}
                aria-label="Close save template modal"
              >
                ×
              </button>
            </div>

            <div style={{ borderTop: `1px solid ${isDark ? '#374151' : '#E5E7EB'}` }} />

            <div style={{ padding: '18px 14px 16px 14px' }}>
              <label
                className="chat_text_sm chat_font_medium"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: isDark ? '#D1D5DB' : '#4B5563',
                }}
              >
                Name Template
              </label>
              <input
                type="text"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                placeholder="Enter Template Name"
                disabled={isSavingTemplate}
                style={{
                  width: '100%',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                  backgroundColor: isDark ? '#111827' : '#FFFFFF',
                  color: isDark ? '#F9FAFB' : '#111827',
                  fontSize: '14px',
                  padding: '11px 12px',
                  outline: 'none',
                }}
              />

              {saveTemplateError && (
                <div
                  className="chat_text_xs"
                  style={{ marginTop: '8px', color: '#EF4444' }}
                >
                  {saveTemplateError}
                </div>
              )}

              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={isSavingTemplate || !saveTemplateName.trim()}
                style={{
                  marginTop: '18px',
                  width: '100%',
                  borderRadius: '12px',
                  border: 'none',
                  padding: '11px 12px',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#FFFFFF',
                  cursor: (isSavingTemplate || !saveTemplateName.trim()) ? 'not-allowed' : 'pointer',
                  background: (isSavingTemplate || !saveTemplateName.trim())
                    ? (isDark ? '#4B5563' : '#D9D6F8')
                    : primaryColor,
                }}
              >
                {isSavingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type || 'error'}
        visible={toast.open}
        duration={2400}
        onClose={() => setToast({ open: false, message: '', type: 'error' })}
      />
    </div>
  );
};

export default OutreachContent;
